import { useState } from "react";
import Modal from "../../../../components/Modal/Modal";
import Button from "../../../../components/Button/Button";
import Input from "../../../../components/Input/Input";

// Which field the student is asking the school to change. Kept as a short,
// fixed list (rather than free text) so the office staff reviewing the
// request immediately know what changed without parsing prose.
const FIELD_OPTIONS = [
  { value: "name", label: "Name" },
  { value: "contact_number", label: "Contact Number" },
  { value: "email", label: "Email" },
  { value: "address", label: "Address" },
  { value: "guardian_details", label: "Parent / Guardian Details" },
  { value: "other", label: "Other" },
];

const EMPTY_FORM = { field: FIELD_OPTIONS[0].value, newValue: "", reason: "" };

/**
 * Small form inside a Modal letting the student ask the school to update one
 * of their profile fields. Purely presentational — the caller owns the
 * submit request (requestProfileEdit) and the open/close state.
 */
const RequestEditModal = ({ isOpen, onClose, onSubmit, isSubmitting }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [touched, setTouched] = useState(false);

  const handleClose = () => {
    if (isSubmitting) return;
    setForm(EMPTY_FORM);
    setTouched(false);
    onClose();
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setTouched(true);
    if (!form.newValue.trim() || !form.reason.trim()) return;

    const fieldLabel = FIELD_OPTIONS.find((option) => option.value === form.field)?.label || form.field;
    onSubmit({
      field: form.field,
      field_label: fieldLabel,
      new_value: form.newValue.trim(),
      reason: form.reason.trim(),
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Request Profile Edit"
      size="sm"
      closable={!isSubmitting}
      closeOnOverlayClick={!isSubmitting}
    >
      <form onSubmit={handleSubmit}>
        <div className="form-group mb-3">
          <label className="mb-1 mt-1 fw-semibold" htmlFor="request-edit-field">
            Field to change <i className="text-danger ms-1">*</i>
          </label>
          <select
            id="request-edit-field"
            className="form-control"
            value={form.field}
            onChange={(event) => setForm((prev) => ({ ...prev, field: event.target.value }))}
            disabled={isSubmitting}
          >
            {FIELD_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <Input
          label="New Value"
          name="newValue"
          value={form.newValue}
          onChangeHandler={(event) => setForm((prev) => ({ ...prev, newValue: event.target.value }))}
          placeholder="Enter the requested new value"
          required
          disabled={isSubmitting}
          error={touched && !form.newValue.trim() ? "New value is required" : ""}
        />

        <div className="form-group mb-3">
          <label className="mb-1 mt-1 fw-semibold" htmlFor="request-edit-reason">
            Reason <i className="text-danger ms-1">*</i>
          </label>
          <textarea
            id="request-edit-reason"
            className={`form-control ${touched && !form.reason.trim() ? "is-invalid" : ""}`}
            rows={3}
            value={form.reason}
            onChange={(event) => setForm((prev) => ({ ...prev, reason: event.target.value }))}
            placeholder="Why do you need this change?"
            disabled={isSubmitting}
          />
          {touched && !form.reason.trim() && (
            <div className="invalid-feedback d-block mt-1">Reason is required</div>
          )}
        </div>

        <div className="d-flex gap-2 mt-3">
          <Button type="button" variant="outline-secondary" className="flex-fill" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" className="flex-fill" loading={isSubmitting}>
            Submit Request
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default RequestEditModal;
